import { useCallback, useEffect, useRef, useState } from "react";

// Countdown hook for the "resend code" cooldown. The backend OTP expires
// after 2 minutes, so the UI enforces a matching 120s cooldown between
// resends — purely presentational, the API itself is unchanged.
export const RESEND_COOLDOWN_SECONDS = 120;

export function formatCountdown(totalSeconds) {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  const latin = `${minutes}:${String(seconds).padStart(2, "0")}`;
  // Render with Persian digits for RTL consistency.
  return latin.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);
}

export function useResendCountdown(initialSeconds = RESEND_COOLDOWN_SECONDS) {
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  const timerRef = useRef(null);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(
    (seconds = initialSeconds) => {
      clear();
      setSecondsLeft(seconds);
      if (seconds <= 0) return;
      timerRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clear();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [clear, initialSeconds]
  );

  useEffect(() => {
    start(initialSeconds);
    return clear;
  }, [start, initialSeconds, clear]);

  const canResend = secondsLeft <= 0;

  return { secondsLeft, canResend, start, clear };
}

export default useResendCountdown;
